import { amenityCrudConfig, type Amenity } from '@/config/objects/amenities';
import { CrudDetailView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Amenity detail page
 */
export function AmenityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  /**
   * Delete amenity mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (amenityId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/amenities/${amenityId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate amenities list to refresh data
        queryClient.invalidateQueries({ queryKey: ['Amenity'] });
        navigate('/amenities');
      } else {
        throw new Error(response.error || 'Failed to delete amenity');
      }
    },
  });

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Amenity Not Found</h2>
          <p className="text-muted-foreground">The requested amenity could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this amenity
   */
  const handleEdit = (amenity: Amenity) => {
    navigate(`/amenities/${amenity.id}/edit`);
  };

  /**
   * Handle amenity deletion
   */
  const handleDelete = async (amenity: Amenity) => {
    return toast.promise(deleteMutation.mutateAsync(amenity.id), {
      loading: 'Deleting amenity...',
      success: 'Amenity deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete amenity',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Amenities', href: '/amenities' },
    { label: 'Amenity', current: true },
  ];

  return (
    <PageContainer>
      <CrudDetailView
        config={amenityCrudConfig}
        itemId={id}
        breadcrumbs={breadcrumbs}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </PageContainer>
  );
}
